import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../master/SideBar";
import { closeModal, SweetAlertShow, LoadingDiv } from "../master/Master";
import Nav from "../master/NavBar";
import Swal from "sweetalert2";
import usePrivateLink from "../config/usePrivateLink";
import StudentsModal from "./StudentsModal";
import Pagination from "../components/Pagination";
import {
  messAdd,
  messDelete,
  messList,
  messStatusChange,
  messUpdate,
} from "../../api/Mess";
import MessLayout from "./MessLayout";

export default function Mess() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const [filters, setFilters] = useState({ page: 1 });
  const [modalData, setModalData] = useState({});
  const [type, setType] = useState("Student");

  const messData = data?.data || [];

  const totalStudents = useMemo(
    () =>
      messData.reduce(
        (total, item) => total + Number(item?.students_count || 0),
        0,
      ),
    [messData],
  );

  const totalStaff = useMemo(
    () =>
      messData.reduce(
        (total, item) => total + Number(item?.staffs_count || 0),
        0,
      ),
    [messData],
  );

  const getMessList = useCallback(async () => {
    setLoading(true);

    try {
      const addUrl = `&page=${filters?.page}`;
      const response = await messList(addUrl);

      if (response?.data?.status) {
        setData(response?.data?.data || {});
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const createMess = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await messAdd(formData);
      if (response?.data?.status) {
        e.target.reset();
        closeModal("addNewMess");
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getMessList();
    }
  };

  const handleStatusChange = async (id) => {
    setLoading(true);
    try {
      const response = await messStatusChange(id);
      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getMessList();
    }
  };

  const updateMess = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData(e.target);
      const response = await messUpdate(formData);
      if (response?.data?.status) {
        closeModal("editMess");
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getMessList();
    }
  };

  const deleteMess = async (id) => {
    setLoading(true);
    try {
      const response = await messDelete(id);
      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
      getMessList();
    }
  };

  const confirmDelete = (id) => {
    Swal.fire({
      title: "Delete Mess?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMess(id);
      }
    });
  };

  const getMessInitial = (name = "") => {
    return name?.trim()?.charAt(0)?.toUpperCase() || "M";
  };

  useEffect(() => {
    getMessList();
  }, [getMessList]);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid py-3">
                {/* =====================================================
                    PAGE HEADER
                ====================================================== */}
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
                  <div>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <div
                        className="rounded d-flex align-items-center justify-content-center bg-label-primary"
                        style={{
                          width: 42,
                          height: 42,
                        }}
                      >
                        <i className="ri-restaurant-2-line ri-22px text-primary" />
                      </div>

                      <div>
                        <h4 className="mb-0 fw-bold">Mess Management</h4>

                        <small className="text-muted">
                          Manage mess facilities, students and staff
                        </small>
                      </div>
                    </div>
                  </div>

                  {hasAccess("/mess/add") && (
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center gap-2"
                      data-bs-toggle="modal"
                      data-bs-target="#addNewMess"
                    >
                      <i className="ri-add-line ri-18px" />
                      Add New Mess
                    </button>
                  )}
                </div>

                <MessLayout />

                {/* =====================================================
                    OVERVIEW
                ====================================================== */}
                <div className="mb-3 mt-4">
                  <h5 className="mb-1 fw-semibold">Overview</h5>

                  <p className="text-muted mb-0">
                    Quick summary of your mess facilities and occupancy.
                  </p>
                </div>

                <div className="row g-3 mb-4">
                  {/* TOTAL MESS */}
                  <div className="col-xl-4 col-md-6">
                    <div className="card h-100 shadow-sm border-0">
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">Total Mess</span>

                            <h3 className="mb-1 mt-2 fw-bold">
                              {data?.total || 0}
                            </h3>

                            <small className="text-muted">
                              Registered mess facilities
                            </small>
                          </div>

                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-label-warning"
                            style={{
                              width: 48,
                              height: 48,
                            }}
                          >
                            <i className="ri-restaurant-2-line ri-24px text-warning" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STUDENTS */}
                  <div className="col-xl-4 col-md-6">
                    <div
                      className="card h-100 shadow-sm border-0"
                      style={{ cursor: "pointer" }}
                      data-bs-toggle="modal"
                      data-bs-target="#StudentData"
                      onClick={() => setType("Student")}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">
                              Total Students
                            </span>

                            <h3 className="mb-1 mt-2 fw-bold">
                              {totalStudents}
                            </h3>

                            <small className="text-primary">
                              <i className="ri-arrow-right-line me-1" />
                              View students
                            </small>
                          </div>

                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-label-primary"
                            style={{
                              width: 48,
                              height: 48,
                            }}
                          >
                            <i className="ri-group-line ri-24px text-primary" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* STAFF */}
                  <div className="col-xl-4 col-md-6">
                    <div
                      className="card h-100 shadow-sm border-0"
                      style={{ cursor: "pointer" }}
                      data-bs-toggle="modal"
                      data-bs-target="#StudentData"
                      onClick={() => setType("Staff")}
                    >
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <span className="text-muted small">
                              Total Staff
                            </span>

                            <h3 className="mb-1 mt-2 fw-bold">{totalStaff}</h3>

                            <small className="text-danger">
                              <i className="ri-arrow-right-line me-1" />
                              View staff
                            </small>
                          </div>

                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center bg-label-danger"
                            style={{
                              width: 48,
                              height: 48,
                            }}
                          >
                            <i className="ri-user-star-line ri-24px text-danger" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =====================================================
                    MESS TABLE
                ====================================================== */}
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-transparent border-bottom py-3">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div>
                        <h5 className="mb-1 fw-semibold">Mess Facilities</h5>

                        <small className="text-muted">
                          Manage all registered mess facilities
                        </small>
                      </div>

                      <span className="badge bg-label-primary rounded-pill px-3 py-2">
                        {data?.total || 0} Total
                      </span>
                    </div>
                  </div>

                  <div className="card-body p-0">
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead>
                          <tr>
                            <th className="ps-4" style={{ width: "70px" }}>
                              #
                            </th>

                            <th>Mess</th>
                            <th>Address</th>
                            <th>Contact</th>
                            <th className="text-center">Status</th>
                            <th className="text-end pe-4">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {messData.length > 0 ? (
                            messData.map((item, index) => (
                              <tr key={item?.id || index}>
                                {/* INDEX */}
                                <td className="ps-4 text-muted fw-medium">
                                  {(filters?.page - 1) * 10 + index + 1}
                                </td>

                                {/* MESS */}
                                <td>
                                  <div className="d-flex align-items-center gap-3">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center bg-label-primary fw-semibold"
                                      style={{
                                        width: 42,
                                        height: 42,
                                        minWidth: 42,
                                      }}
                                    >
                                      {getMessInitial(item?.name)}
                                    </div>

                                    <div>
                                      <div className="fw-semibold">
                                        {item?.name || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </td>

                                {/* ADDRESS */}
                                <td>
                                  <div
                                    className="text-muted"
                                    style={{
                                      maxWidth: 280,
                                      whiteSpace: "normal",
                                    }}
                                  >
                                    {item?.address ? (
                                      <>
                                        <i className="ri-map-pin-line me-1 text-primary" />
                                        {item.address}
                                      </>
                                    ) : (
                                      "-"
                                    )}
                                  </div>
                                </td>

                                {/* CONTACT */}
                                <td>
                                  {item?.mobile ? (
                                    <div className="d-flex align-items-center gap-2">
                                      <span className="avatar avatar-xs">
                                        <span className="avatar-initial rounded bg-label-success">
                                          <i className="ri-phone-line ri-14px" />
                                        </span>
                                      </span>

                                      <span>{item.mobile}</span>
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </td>

                                {/* STATUS */}
                                <td className="text-center">
                                  {hasAccess("/mess/status") ? (
                                    <div className="d-flex align-items-center justify-content-center gap-2">
                                      <div className="form-check form-switch mb-0">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          role="switch"
                                          checked={
                                            item?.status === "1" ||
                                            item?.status === 1
                                          }
                                          onChange={() =>
                                            handleStatusChange(item?.id)
                                          }
                                        />
                                      </div>

                                      <span
                                        className={`badge rounded-pill ${
                                          item?.status === "1" ||
                                          item?.status === 1
                                            ? "bg-label-success"
                                            : "bg-label-secondary"
                                        }`}
                                      >
                                        {item?.status === "1" ||
                                        item?.status === 1
                                          ? "Active"
                                          : "Inactive"}
                                      </span>
                                    </div>
                                  ) : (
                                    <span
                                      className={`badge rounded-pill ${
                                        item?.status === "1" ||
                                        item?.status === 1
                                          ? "bg-label-success"
                                          : "bg-label-secondary"
                                      }`}
                                    >
                                      {item?.status === "1" ||
                                      item?.status === 1
                                        ? "Active"
                                        : "Inactive"}
                                    </span>
                                  )}
                                </td>

                                {/* ACTIONS */}
                                <td className="text-end pe-4">
                                  <div className="d-flex justify-content-end gap-1">
                                    {hasAccess("/mess/edit") && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-text-info rounded-pill"
                                        title="Edit Mess"
                                        data-bs-toggle="modal"
                                        data-bs-target="#editMess"
                                        onClick={() => setModalData(item)}
                                      >
                                        <i className="ri-edit-line ri-18px" />
                                      </button>
                                    )}

                                    {hasAccess("/mess/delete") && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-icon btn-text-danger rounded-pill"
                                        title="Delete Mess"
                                        onClick={() => confirmDelete(item?.id)}
                                      >
                                        <i className="ri-delete-bin-line ri-18px" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6">
                                <div className="text-center py-5">
                                  <div
                                    className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center mx-auto mb-3"
                                    style={{
                                      width: 70,
                                      height: 70,
                                    }}
                                  >
                                    <i className="ri-restaurant-2-line ri-32px text-primary" />
                                  </div>

                                  <h6 className="mb-1">No Mess Found</h6>

                                  <p className="text-muted mb-3">
                                    There are no mess facilities available at
                                    the moment.
                                  </p>

                                  {hasAccess("/mess/add") && (
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      data-bs-toggle="modal"
                                      data-bs-target="#addNewMess"
                                    >
                                      <i className="ri-add-line me-1" />
                                      Add Mess
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {messData.length > 0 && (
                      <div className="border-top px-4 py-3">
                        <Pagination
                          data={data}
                          filters={filters}
                          setFilters={setFilters}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StudentsModal type={type} />

      <div
        className="modal fade"
        id="addNewMess"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content border-0 shadow">
            <form onSubmit={createMess}>
              <div className="modal-header border-bottom">
                <div>
                  <h5 className="modal-title mb-1">
                    <i className="ri-restaurant-2-line me-2 text-primary" />
                    Add New Mess
                  </h5>

                  <small className="text-muted">
                    Enter the details of the new mess facility.
                  </small>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>

              <div className="modal-body p-4">
                <div className="form-floating form-floating-outline mb-3">
                  <input
                    type="text"
                    className="form-control input-uppercase"
                    placeholder="Enter Mess Name"
                    name="name"
                    required
                  />

                  <label>
                    Mess Name <span className="text-danger">*</span>
                  </label>
                </div>

                <div className="form-floating form-floating-outline mb-3">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Contact Number"
                    name="mobile"
                  />

                  <label>Contact Number</label>
                </div>

                <div className="form-floating form-floating-outline">
                  <textarea
                    className="form-control input-uppercase"
                    placeholder="Enter Mess Address"
                    name="address"
                    style={{ minHeight: 100 }}
                  />

                  <label>Address</label>
                </div>
              </div>

              <div className="modal-footer border-top bg-light-subtle">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="modal"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                  )}
                  {loading ? "Saving..." : "Save Mess"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="editMess"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow">
            <div className="modal-header border-bottom">
              <div>
                <h5 className="modal-title mb-1">
                  <i className="ri-edit-line me-2 text-primary" />
                  Edit Mess
                </h5>
                <small className="text-muted">
                  Update mess facility information.
                </small>
              </div>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <form onSubmit={updateMess}>
              <div className="modal-body p-4">
                <input type="hidden" name="id" value={modalData?.id || ""} />
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control input-uppercase"
                        placeholder="Enter Mess Name"
                        name="name"
                        defaultValue={modalData?.name || ""}
                        required
                      />
                      <label>
                        Mess Name <span className="text-danger">*</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Contact Number"
                        name="mobile"
                        defaultValue={modalData?.mobile || ""}
                      />
                      <label>Contact Number</label>
                    </div>
                  </div>
                  <div className="col-12">
                    <div className="form-floating form-floating-outline">
                      <textarea
                        className="form-control input-uppercase"
                        placeholder="Enter Mess Address"
                        name="address"
                        defaultValue={modalData?.address || ""}
                        style={{ minHeight: 120 }}
                      />
                      <label>Address</label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-top bg-light-subtle">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  data-bs-dismiss="modal"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading && (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                  )}
                  {loading ? "Updating..." : "Update Mess"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
